import { ETAPES, type Etape } from './etapes.js';
import { TAILLE_CIBLE, indexerFoyers, membresPresents, plageDe, type Cible } from './foyers.js';
import type { FoyersEnDouble, Foyer, Groupe, GroupeEvalue, PaireEnDouble, RencontreLieu, Score } from './types.js';

const cle = (a: string, b: string): string => (a < b ? `${a}|${b}` : `${b}|${a}`);

/**
 * Score affiché (écran 5) : rencontres uniques / totales, paires en double,
 * groupes hors plage, foyers réunis. Fonction pure, utilisable dans le navigateur
 * pour un recalcul instantané après un déplacement manuel.
 */
export function calculerScore(foyers: Foyer[], groupes: Groupe[], cible: Cible = TAILLE_CIBLE): Score {
  const plage = plageDe(cible);
  const parId = indexerFoyers(foyers);
  const foyerDe = new Map<string, string>();
  const personnes = new Map<string, Foyer['membres'][number]>();
  for (const f of foyers) {
    for (const p of membresPresents(f)) {
      foyerDe.set(p.id, f.id);
      personnes.set(p.id, p);
    }
  }

  const compte = new Map<string, number>();
  const lieux = new Map<string, RencontreLieu[]>();
  const groupesEvalues: GroupeEvalue[] = [];
  let groupesHorsPlage = 0;

  for (const g of groupes) {
    const ids: string[] = [];
    for (const fid of g.foyerIds) {
      const f = parId.get(fid);
      if (!f) continue;
      for (const p of membresPresents(f)) ids.push(p.id);
    }
    const taille = ids.length;
    const horsPlage = taille < plage.min || taille > plage.max;
    if (horsPlage) groupesHorsPlage++;
    const capaciteMax = parId.get(g.hoteFoyerId)?.capaciteMax;
    groupesEvalues.push({ ...g, taille, horsPlage, capaciteMax, capaciteDepassee: capaciteMax !== undefined && taille > capaciteMax });

    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        const a = ids[i] as string;
        const b = ids[j] as string;
        if (foyerDe.get(a) === foyerDe.get(b)) continue;
        const k = cle(a, b);
        compte.set(k, (compte.get(k) ?? 0) + 1);
        const l = lieux.get(k) ?? [];
        l.push({ etape: g.etape, hoteFoyerId: g.hoteFoyerId });
        lieux.set(k, l);
      }
    }
  }

  let rencontresTotales = 0;
  let doublons = 0;
  const pairesEnDouble: PaireEnDouble[] = [];
  const parFoyers = new Map<string, FoyersEnDouble>();
  for (const [k, n] of compte) {
    rencontresTotales += n;
    if (n <= 1) continue;
    doublons += n - 1;
    const [a, b] = k.split('|') as [string, string];
    const foyerA = foyerDe.get(a) as string;
    const foyerB = foyerDe.get(b) as string;
    const l = lieux.get(k) ?? [];
    pairesEnDouble.push({ personneA: personnes.get(a)!, personneB: personnes.get(b)!, foyerA, foyerB, lieux: l });
    const kf = cle(foyerA, foyerB);
    const existant = parFoyers.get(kf);
    if (existant) existant.nombrePaires++;
    else parFoyers.set(kf, { foyerA: foyerA < foyerB ? foyerA : foyerB, foyerB: foyerA < foyerB ? foyerB : foyerA, lieux: l, nombrePaires: 1 });
  }

  // Règle 3 : les membres présents d'un même foyer (2 ou 3 personnes) sont dans le même groupe à chaque étape.
  const foyersAPlusieurs = foyers.filter((f) => membresPresents(f).length >= 2);
  let reunis = 0;
  for (const f of foyersAPlusieurs) {
    const ok = ETAPES.every((e) => groupes.filter((g) => g.etape === e && g.foyerIds.includes(f.id)).length === 1);
    if (ok) reunis++;
  }

  return {
    rencontresUniques: compte.size,
    rencontresTotales,
    doublons,
    pairesEnDouble,
    foyersEnDouble: [...parFoyers.values()],
    groupesHorsPlage,
    groupes: groupesEvalues,
    foyersReunis: { reunis, total: foyersAPlusieurs.length },
  };
}

/** Pour chaque étape, les tailles (en personnes) des groupes. */
export function taillesParEtape(score: Score): Record<Etape, number[]> {
  const r: Record<Etape, number[]> = { entree: [], plat: [], dessert: [] };
  for (const g of score.groupes) r[g.etape].push(g.taille);
  return r;
}
